import React, { useState, useEffect, useRef } from "react";
import "./css/Solutions.css";
import { Link } from "react-router-dom";

export const Solutions = () => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const carouselTrackRef = useRef(null);

    // Data for the cards
    const cardsData = [
        {
            title: "Fleet Owners",
            description: "Empowering your fleet with cost-effective solutions for a seamless transition to electric trucks at one click.",
            features: [
                {
                    title: "Route optimization",
                    content: "Assess the feasibility of converting to electric trucks based on specific routes."
                },
                {
                    title: "Charging strategy",
                    content: "Determine the best times and locations for charging using TOU rates and available third-party charging stations."
                },
                {
                    title: "Depot charging potential",
                    content: "Evaluate the benefits and logistics of setting up depot charging stations."
                },
                {
                    title: "Renewable integration",
                    content: "Explore the potential for integrating solar power and battery storage to reduce costs and enhance sustainability."
                }
            ]
        },
        {
            title: "Charging Station Developers",
            description: "Strategic guide to optimal locations with data-driven insights and growth potential.",
            features: [
                {
                    title: "Optimal locations",
                    content: "Identify the best sites for new charging stations."
                },
                {
                    title: "Power capacity",
                    content: "Assess power system capacity availability."
                },
                {
                    title: "Data-driven",
                    content: "Leverage extensive truck trajectory datasets."
                },
                {
                    title: "Future-proof",
                    content: "Plan for scalable and sustainable infrastructure."
                }
            ]
        },
        {
            title: "Utilities",
            description: "Plan for fleet-scale charging, optimize grid capacity, and leverage V2G for efficiency.",
            features: [
                {
                    title: "Grid reinforcement",
                    content: "Identify when, where, and how much grid reinforcement is needed."
                },
                {
                    title: "Data-driven decisions",
                    content: "Utilize extensive datasets on truck trajectories, charging stations, and power systems."
                },
                {
                    title: "V2G potential",
                    content: "Evaluate vehicle-to-grid opportunities to minimize upgrades."
                },
                {
                    title: "Advanced integration",
                    content: "Advanced spatio-temporal optimization models for precise planning and efficiency."
                }
            ]
        }
    ];

    const totalCards = cardsData.length;

    // Auto-scroll effect
    useEffect(() => {
        const interval = setInterval(() => {
            setActiveIndex((prevIndex) => (prevIndex + 1) % totalCards);
        }, 5000); // Change card every 5 seconds

        return () => clearInterval(interval);
    }, [totalCards]);

    // Manual navigation
    const nextSlide = () => {
        setActiveIndex((prevIndex) => (prevIndex + 1) % totalCards);
    };

    const prevSlide = () => {
        setActiveIndex((prevIndex) => (prevIndex - 1 + totalCards) % totalCards);
    };

    const goToSlide = (index) => {
        setActiveIndex(index);
    };

    return (
        <div>
            <div id="solutions" className="solutions-container" >
                <div className="solutions-content">
                    <div className="solutions-content-left">
                        <h1 className="solutions-content-left-title">Our solutions</h1>
                        <p className="solutions-content-left-description">We foster a deep understanding of the electric ecosystem through advanced modeling, AI, and data analysis - key to coordinating development effectively and efficiently.</p>
                        <p className="solutions-content-left-description">Fleet electrification is a complex process that requires a deep understanding of the electric ecosystem. We leverage advanced modeling, AI, and data analysis to coordinate development effectively and efficiently.</p>
                        <Link to="/subscription" className="btn btn-custom get-started-button">
                            Get started
                        </Link>
                    </div>
                    <div className="solutions-content-right">
                        <img className="solutions-content-right-image" src='img/homepage/charging-infrastructure-homepage.png' alt="trucks" />
                    </div>
                </div>
            </div>

            <div style={{ position: "relative" }}>
                <div className="solutions-overlay-background">
                    <svg width="100%" height="auto" viewBox="0 0 1920 1222" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1920 1135.96C1388.5 1273.31 590 1225.59 0 1135.96V133.784C341 -4.49655 1019.5 -80.6716 1920 133.784V1135.96Z" fill="#0078D4" />
                    </svg>
                </div>

                <div className="solutions-overlay-background-mobile">
                    <svg width="100%" height="auto" viewBox="0 0 414 1097" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M414 1052.51C282 1124.5 134.5 1096.5 0 1052.51V68.3782C95 -10.5003 190.5 -33.5 414 68.3782V1052.51Z" fill="#0078D4" />
                    </svg>
                </div>

                <div className="solutions-overlay">
                    <div className="solutions-overlay-content">
                        <h1>Why our clients are choosing AmpTrans</h1>

                        <div className="solutions-carousel-container">
                            <div
                                ref={carouselTrackRef}
                                className="solutions-carousel-track"
                                style={{
                                    transform: `translateX(-${activeIndex * 100}%)`,
                                }}
                            >
                                {cardsData.map((card, index) => (
                                    <div
                                        className="solutions-feature-box carousel-card"
                                        key={index}
                                    >
                                        <h3>{card.title}</h3>
                                        <p>{card.description}</p>
                                        <div className="solutions-feature-details">
                                            {card.features.map((feature, featureIndex) => (
                                                <div className="solutions-feature-item" key={featureIndex}>
                                                    <span>{feature.title}</span>
                                                    <p>{feature.content}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="carousel-navigation">
                                {cardsData.map((_, index) => (
                                    <button
                                        key={index}
                                        className={`carousel-dot ${activeIndex === index ? 'active' : ''}`}
                                        onClick={() => goToSlide(index)}
                                        aria-label={`Go to slide ${index + 1}`}
                                    />
                                ))}
                            </div>

                            <button
                                className="carousel-arrow carousel-arrow-prev"
                                onClick={prevSlide}
                                aria-label="Previous slide"
                            >
                                &#10094;
                            </button>
                            <button
                                className="carousel-arrow carousel-arrow-next"
                                onClick={nextSlide}
                                aria-label="Next slide"
                            >
                                &#10095;
                            </button>
                        </div>
                    </div>
                </div>

                <div className="solutions-calculator-container">
                    <div className="solutions-calculator-content">
                        <h1>Find the tool that best fits your fleet today</h1>
                        <div className="solutions-calculator-content-button">
                            <Link to="/calculator" className="btn btn-custom">Explore your options</Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}